import React, { useEffect, useMemo } from 'react';
import { Box, Button, Flex, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BountyCategory } from '@foodfluence/shared';
import { useBountyData } from '../../../contexts/BountyDataContext';
import { ff } from '../../../theme';
import { DisplayHeading } from '../../../components/ui';
import BrandTile from '../components/BrandTile';
import Pill from '../components/Pill';
import BountyBackButton from '../components/BountyBackButton';
import StickyBottomCta from '../components/StickyBottomCta';
import { myBountiesPath, proposalStatusChip, resubmitProposalPath } from '../utils';
import { colorSchemeToTone, eyebrowStyle, pageBottomPaddingWithStickyCta } from '../theme';
import successIcon from '../assets/success.png';
import emptyMark from '../assets/empty-mark.png';

/** Values handed over by ProposeBountyPage so the screen renders before any refetch lands. */
type SubmittedProposalState = {
  companyName: string;
  website: string;
  category: BountyCategory;
  instagramHandle: string;
  proposalNote: string;
};

const CATEGORY_LABELS: Record<BountyCategory, string> = {
  cpgBrand: 'CPG Brands',
  restaurant: 'Restaurants',
  hotelsLodging: 'Hotels & lodging',
  other: 'Other',
};

const NEXT_STEPS = [
  "We'll review your submission",
  "If it's a match, we'll reach out",
  'You could earn a reward for a successful intro 🎉',
];

const ProposalSubmittedPage: React.FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const { state } = useLocation() as { state: SubmittedProposalState | null };
  const { proposals, loading } = useBountyData();
  const navigate = useNavigate();

  const proposal = useMemo(() => proposals.find(p => p.id === proposalId), [proposals, proposalId]);
  const canEdit = proposal?.status === 'pendingReview';

  // Prefer what was just submitted; fall back to the stored bounty on reload.
  const submitted: SubmittedProposalState | null = useMemo(() => {
    if (state?.companyName) return state;
    if (!proposal) return null;
    return {
      companyName: proposal.companyName,
      website: proposal.website ?? '',
      category: proposal.category,
      instagramHandle: proposal.instagramHandle ?? '',
      proposalNote: proposal.proposalNote ?? '',
    };
  }, [state, proposal]);

  // On a cold reload there is no router state, so wait for the bounty fetch to settle
  // before deciding there is nothing to show.
  useEffect(() => {
    if (!submitted && !loading) navigate(myBountiesPath, { replace: true });
  }, [submitted, loading, navigate]);

  if (!submitted) {
    return loading ? (
      <Flex minH="100svh" align="center" justify="center" bg="white">
        <Spinner color={ff.muted} />
      </Flex>
    ) : null;
  }

  const handle = submitted.instagramHandle ? `@${submitted.instagramHandle.replace(/^@+/, '')}` : '';
  const meta = [CATEGORY_LABELS[submitted.category], submitted.website, handle].filter(Boolean).join(' · ');
  const chip = proposal ? proposalStatusChip(proposal) : proposalStatusChip({ status: 'pendingReview' });
  const tone = proposal?.status === 'rejected' ? 'amber' : colorSchemeToTone(chip.colorScheme);

  return (
    <Box minH="100svh" bg="white" fontFamily="display" color={ff.ink} lineHeight="normal" px="20px" pt="12px" pb={pageBottomPaddingWithStickyCta} overflowX="hidden">
      <Box ml="-8px">
        <BountyBackButton to={myBountiesPath} replace aria-label="Back to My Bounties" />
      </Box>
      <VStack spacing="20px" align="stretch" pt="8px">
        <Image src={successIcon} alt="" w="64px" h="64px" objectFit="contain" flex="none" />
        <Box>
          <DisplayHeading size="31px">Bounty submitted!</DisplayHeading>
          <Text fontSize="15px" lineHeight="1.45" color="#3E3E42" mt="10px">
            Thanks for the intro! Our team will review your submission and reach out if it&apos;s a great fit.{' '}
            <Text as="strong" color={ff.ink}>You&apos;ll hear back within 2–3 business days.</Text>
          </Text>
        </Box>

        <Flex direction="column" gap="12px" p="16px" border="1px solid" borderColor="#E4E4E0" borderRadius="18px">
          <Text as="h2" {...eyebrowStyle}>Your submission</Text>
          <Flex align="center" gap="12px">
            <BrandTile name={submitted.companyName} logoUrl={proposal?.logoUrl} size={44} radius={12} fontSize={14} />
            <Flex direction="column" gap="2px" flex={1} minW={0}>
              <Text as="strong" fontSize="16px" fontWeight={600} noOfLines={1}>{submitted.companyName}</Text>
              <Text fontSize="13px" color="#55555A" wordBreak="break-word">{meta}</Text>
            </Flex>
            <Pill label={chip.label} tone={tone} />
          </Flex>
          <Flex direction="column" gap="4px" pt="12px" borderTop="1px solid" borderColor={ff.line}>
            <Text textStyle="eyebrow" fontSize="12px" letterSpacing="0.08em" color={ff.text2}>Who do you know there?</Text>
            <Text fontSize="14px" lineHeight="1.45" color="#2A2A2C" wordBreak="break-word">{submitted.proposalNote}</Text>
          </Flex>
          {canEdit && proposalId && (
            <Button
              variant="inkOutline"
              alignSelf="flex-start"
              h="44px"
              px="16px"
              borderRadius="full"
              fontSize="14px"
              onClick={() => navigate(`/bounties/propose?edit=${proposalId}`, { replace: true })}
            >
              Edit submission
            </Button>
          )}
          {proposal?.status === 'rejected' && proposalId && (
            <Button
              variant="inkOutline"
              alignSelf="flex-start"
              h="44px"
              px="16px"
              borderRadius="full"
              fontSize="14px"
              onClick={() => navigate(resubmitProposalPath(proposalId), { replace: true })}
            >
              Resubmit
            </Button>
          )}
        </Flex>

        <Flex gap="12px" align="flex-start" p="16px" borderRadius="18px" bg={ff.limeSurface}>
          <Image src={emptyMark} alt="" w="28px" h="28px" objectFit="contain" flex="none" />
          <Box>
            <Text as="h2" fontSize="15px" fontWeight={600} color={ff.ink}>What happens next?</Text>
            <VStack as="ul" align="stretch" spacing="6px" mt="6px" listStyleType="none">
              {NEXT_STEPS.map(step => (
                <Text as="li" key={step} fontSize="14px" lineHeight="1.45" color="#2A2A2C">{step}</Text>
              ))}
            </VStack>
          </Box>
        </Flex>
      </VStack>

      <StickyBottomCta>
        <Button
          variant="ink"
          w="full"
          h="56px"
          borderRadius="full"
          fontSize="16px"
          color={ff.lime}
          onClick={() => navigate(myBountiesPath, { replace: true })}
        >
          Back to Bounties
        </Button>
      </StickyBottomCta>
    </Box>
  );
};

export default ProposalSubmittedPage;
